/**
 * One client's receivables, end to end — every project they have, and the
 * draw schedule, change orders, and invoices behind whichever of those
 * projects is on screen. The landing Billing page groups its project figures
 * by client precisely so this page can be the drill-down: the same billing
 * panels Billing used to show for one project picked out of the whole
 * company, now scoped to one client's own projects instead.
 */
import { ZERO, add } from "@dantria/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, fmtAmount as fmt, unwrap } from "../api";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  Stat,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { PortalAccess, canManagePortalAccess } from "./Clients";
import { ChangeOrders, Invoices } from "./shared";

const STATUS_STYLE: Record<string, string> = {
  planned: "text-neutral-500",
  ready: "text-neutral-900",
  invoiced: "text-blue-700",
  paid: "text-green-700",
  void: "text-neutral-400 line-through",
};

export function ClientDetail({
  slug,
  clientId,
  currency,
  role,
  onBack,
}: {
  slug: string;
  clientId: string;
  currency: string;
  role: string;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const del = useDeleteRecord(slug);
  const [projectId, setProjectId] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The client's own record — name, terms — has no single-row fetch, so it's
  // found in the same list the landing page already reads.
  const clients = useQuery({
    queryKey: ["clients", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].clients.$get({ param: { slug } })),
  });
  const client = clients.data?.clients.find((c) => c.id === clientId);

  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
  });
  const clientProjects = (projects.data?.projects ?? []).filter((p) => p.clientId === clientId);

  const overview = useQuery({
    queryKey: ["reports-overview", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].reports.overview.$get({ param: { slug }, query: {} })),
  });

  // Contract value (original plus approved change orders) lives on
  // `profitByProject`; invoiced/paid/outstanding come off the ledger-backed
  // rows — the same two sources the landing page's client totals use, so the
  // two screens can never disagree.
  const contractByProject = new Map(
    (overview.data?.profitByProject ?? []).map((p) => [p.projectId, p.contractValue]),
  );
  const ledgerByProject = new Map((overview.data?.projects ?? []).map((p) => [p.projectId, p]));
  const totals = clientProjects.reduce(
    (acc, p) => {
      const ledger = ledgerByProject.get(p.id);
      return {
        contract: add(acc.contract, contractByProject.get(p.id) ?? p.originalContractValue),
        invoiced: add(acc.invoiced, ledger?.invoiced ?? ZERO),
        paid: add(acc.paid, ledger?.cashIn ?? ZERO),
        outstanding: add(acc.outstanding, ledger?.ar ?? ZERO),
      };
    },
    { contract: ZERO, invoiced: ZERO, paid: ZERO, outstanding: ZERO },
  );

  const active = projectId || clientProjects[0]?.id || "";

  const schedule = useQuery({
    queryKey: ["schedule", slug, active],
    enabled: !!active,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].billing[":projectId"].schedule.$get({
          param: { slug, projectId: active },
        }),
      ),
  });

  const changeOrders = useQuery({
    queryKey: ["change-orders", slug, active],
    enabled: !!active,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].billing[":projectId"]["change-orders"].$get({
          param: { slug, projectId: active },
        }),
      ),
  });

  const invoices = useQuery({
    queryKey: ["invoices", slug, active],
    enabled: !!active,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].invoices.$get({ param: { slug }, query: { projectId: active } }),
      ),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["schedule", slug, active] });
    qc.invalidateQueries({ queryKey: ["change-orders", slug, active] });
    qc.invalidateQueries({ queryKey: ["invoices", slug, active] });
    // A payment moves cash and the receivable, so the reports are stale too.
    qc.invalidateQueries({ queryKey: ["payments", slug] });
    qc.invalidateQueries({ queryKey: ["reports-overview", slug] });
  }

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  const invoiceMilestone = (milestoneId: string) =>
    run.mutate(() =>
      unwrap(
        api.api.c[":slug"].invoices.$post({
          param: { slug },
          json: { projectId: active, milestoneId },
        }),
      ),
    );

  const drawScheduleTable = useTable([...(schedule.data?.schedule.milestones ?? [])], {
    filter: (m, q) => `${m.name} ${m.status}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      seq: (m) => m.seq,
      milestone: (m) => m.name.toLowerCase(),
      status: (m) => m.status,
      amount: (m) => Number(m.amount),
    },
  });

  if (clients.isPending || projects.isPending) return <Loading />;

  const sched = schedule.data?.schedule;

  return (
    <>
      <nav className="sticky top-14 z-20 -mx-6 mb-4 border-b border-neutral-200 bg-neutral-50/90 px-6 pb-3 pt-3 backdrop-blur-md">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-base font-semibold tracking-tight">{client?.name ?? "Client"}</h1>
            {client && <p className="text-xs text-neutral-500">terms {client.paymentTermsDays}d</p>}
          </div>
          <button
            type="button"
            onClick={onBack}
            className="mt-0.5 whitespace-nowrap text-sm text-neutral-500 transition-colors hover:text-neutral-900"
          >
            ← Billing
          </button>
        </div>
      </nav>

      {error && <ErrorNote>{error}</ErrorNote>}
      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        <Stat
          label="Contract"
          value={fmt(totals.contract)}
          currency={currency}
          hint="Across every project"
        />
        <Stat label="Billed" value={fmt(totals.invoiced)} currency={currency} />
        <Stat label="Paid" value={fmt(totals.paid)} currency={currency} />
        <Stat
          label="Outstanding"
          value={fmt(totals.outstanding)}
          currency={currency}
          hint="Billed, not yet paid"
          emphasis
        />
      </div>

      {client && canManagePortalAccess(role) && (
        <Panel title="Portal access">
          <PortalAccess slug={slug} clientId={clientId} email={client.email} />
        </Panel>
      )}

      {clientProjects.length === 0 ? (
        <Empty title="No projects yet">Nothing to bill for this client.</Empty>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-end gap-3">
            <Field label="Project">
              <select
                className={input}
                value={active}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {clientProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.name}
                  </option>
                ))}
              </select>
            </Field>
            {sched && (
              <dl className="flex gap-6 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-neutral-500">Contract</dt>
                  <dd className="tabular">{fmt(sched.revisedContractValue)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-neutral-500">Scheduled</dt>
                  <dd className="tabular">{fmt(sched.scheduledTotal)}</dd>
                </div>
              </dl>
            )}
          </div>

          <Panel title="Draw schedule">
            {schedule.isPending ? (
              <Loading />
            ) : !sched || sched.milestones.length === 0 ? (
              <Empty title="No schedule yet">
                A draw schedule splits the contract into the payments you'll invoice.
              </Empty>
            ) : (
              <>
                <TableSearch
                  value={drawScheduleTable.query}
                  onChange={drawScheduleTable.setQuery}
                  placeholder="Filter milestones…"
                />
                <Table
                  head={
                    <>
                      <Th
                        sortKey="seq"
                        active={drawScheduleTable.sortKey === "seq"}
                        dir={drawScheduleTable.dir}
                        onSort={drawScheduleTable.toggleSort}
                      >
                        #
                      </Th>
                      <Th
                        sortKey="milestone"
                        active={drawScheduleTable.sortKey === "milestone"}
                        dir={drawScheduleTable.dir}
                        onSort={drawScheduleTable.toggleSort}
                      >
                        Milestone
                      </Th>
                      <Th
                        sortKey="status"
                        active={drawScheduleTable.sortKey === "status"}
                        dir={drawScheduleTable.dir}
                        onSort={drawScheduleTable.toggleSort}
                      >
                        Status
                      </Th>
                      <Th
                        right
                        sortKey="amount"
                        active={drawScheduleTable.sortKey === "amount"}
                        dir={drawScheduleTable.dir}
                        onSort={drawScheduleTable.toggleSort}
                      >
                        Amount
                      </Th>
                      <Th />
                    </>
                  }
                >
                  {drawScheduleTable.rows.map((m) => (
                    <tr
                      key={m.id}
                      className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                    >
                      <Td mono muted>
                        {m.seq}
                      </Td>
                      <Td>{m.name}</Td>
                      <Td>
                        <span className={STATUS_STYLE[m.status] ?? ""}>{m.status}</span>
                        {m.frozen && <span className="ml-2 text-xs text-neutral-500">frozen</span>}
                      </Td>
                      <Td right mono>
                        {fmt(m.amount)}
                      </Td>
                      <Td right>
                        {!m.frozen && (
                          <Button
                            variant="ghost"
                            disabled={run.isPending}
                            onClick={() => invoiceMilestone(m.id)}
                          >
                            Invoice
                          </Button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </Table>
                {sched.unallocated !== "0.00" && (
                  <ErrorNote>
                    {fmt(sched.unallocated)} of the contract has no open milestone to absorb it.
                  </ErrorNote>
                )}
              </>
            )}
          </Panel>

          <ChangeOrders
            currency={schedule.data?.project.currency ?? ""}
            settlements={schedule.data?.changeOrders.settlements ?? {}}
            pendingIds={schedule.data?.changeOrders.pending ?? []}
            onInvoice={(id) =>
              run.mutate(() =>
                unwrap(
                  api.api.c[":slug"].invoices.$post({
                    param: { slug },
                    json: { projectId: active, milestoneId: null, changeOrderIds: [id] },
                  }),
                ),
              )
            }
            onRecordPayment={(inv, p) =>
              run.mutate(() =>
                unwrap(
                  api.api.c[":slug"].payments.$post({
                    param: { slug },
                    json: {
                      clientId: inv.partyId,
                      receivedOn: p.on,
                      amount: p.amount,
                      currency: schedule.data?.project.currency ?? "",
                      ...(p.method ? { method: p.method } : {}),
                      ...(p.reference ? { reference: p.reference } : {}),
                      applications: [{ invoiceId: inv.id, amount: p.amount }],
                    },
                  }),
                ),
              )
            }
            rows={changeOrders.data?.changeOrders ?? []}
            pending={changeOrders.isPending}
            busy={run.isPending}
            onCreate={(body) =>
              run.mutate(() =>
                unwrap(
                  api.api.c[":slug"].billing[":projectId"]["change-orders"].$post({
                    param: { slug, projectId: active },
                    json: body,
                  }),
                ),
              )
            }
            onApprove={(id) =>
              run.mutate(() =>
                unwrap(
                  api.api.c[":slug"].billing[":projectId"]["change-orders"][":id"].$patch({
                    param: { slug, projectId: active, id },
                    json: { status: "approved" },
                  }),
                ),
              )
            }
            {...(role === "owner" && { onDelete: (id: string) => del.remove("change_order", id) })}
          />

          <Invoices
            rows={invoices.data?.invoices ?? []}
            pending={invoices.isPending}
            busy={run.isPending}
            onRecordPayment={(body) =>
              run.mutate(() =>
                unwrap(
                  api.api.c[":slug"].payments.$post({
                    param: { slug },
                    json: body,
                  }),
                ),
              )
            }
            {...(role === "owner" && { onDelete: (id: string) => del.remove("invoice", id) })}
          />
        </>
      )}
    </>
  );
}
